import AppsIcon from '@mui/icons-material/Apps';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { SCAN_PIPELINE_STATUSES_STRING } from '../../../../../services/entities/impl/blfApi/schemas/scan';
import { useTranslation } from '../../../../../services/translation/hooks/useTranslation';
import { DiagnosticFeature } from '../../../../commonUI/viewMode/DiagnosticFeature';
export const TooltipScanSearch = () => {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 2, overflow: 'hidden', maxWidth: 280, p: 1 }}
    >
      <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
          sx={{ mb: 0.75 }}
        >
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
            {t('Scan Search Options')}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={0.75}>
          <SearchField
            icon={<LocationOnIcon fontSize='small' />}
            label={t('Customer Name')}
          />

          <SearchField
            icon={<LocationOnIcon fontSize='small' />}
            label={t('Ranch Name')}
          />

          <SearchField
            icon={<LocationOnIcon fontSize='small' />}
            label={t('Block Name')}
          />

          <SearchField
            icon={<CalendarTodayIcon fontSize='small' />}
            label={t('Scan Date')}
            format='YYYY-MM-DD'
            examples={['2025-03-10, 2025, 3-10']}
          />

          <SearchField
            icon={<CameraAltIcon fontSize='small' />}
            label={`${t('Camera')} ID`}
            examples={['A100']}
          />

          {/* QA Mode Fields */}
          <DiagnosticFeature>
            {/* Divider margin not working without box wrapper here */}
            <Box>
              <Divider sx={{ my: 2 }} />
            </Box>

            <CategoryTitle>{t('Advanced Fields')}</CategoryTitle>

            <SearchField
              icon={<CodeIcon fontSize='small' />}
              label={t('Flash Version')}
              examples={['9.27.16']}
            />

            <SearchField
              icon={<CodeIcon fontSize='small' />}
              label={`BloomGo ${t('Version')}`}
              examples={['9.39.0']}
            />

            <SearchField
              icon={<DataObjectIcon fontSize='small' />}
              label={t('Pipeline Status')}
              examples={SCAN_PIPELINE_STATUSES_STRING}
            />

            <SearchField
              icon={<AppsIcon fontSize='small' />}
              label={t('Scan Name')}
              examples={['NCI, HTF:2025-01-21:ESP']}
            />

            <SearchField
              icon={<LocationOnIcon fontSize='small' />}
              label={t('Customer Short Code')}
              examples={['HTF, RBF']}
            />

            {/* IDs row */}
            <Box sx={{ mt: 0.5 }}>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 0.5, display: 'block' }}
              >
                {`ID ${t('Fields')}:`}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <IdField label={`${t('Customer')} ID`} />
                <IdField label={`${t('Ranch')} ID`} />
                <IdField label={`${t('Block')} ID`} />
                <IdField label={`${t('Collection')} ID`} />
                <IdField label={`${t('Scan')} ID`} />
              </Box>
            </Box>
          </DiagnosticFeature>
        </Stack>
      </Box>
    </Paper>
  );
};

const CategoryTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant='caption'
    fontWeight={600}
    color='primary'
    sx={{ mb: 0.5, display: 'block' }}
  >
    {children}
  </Typography>
);

const IdField = ({ label }: { label: string }) => (
  <Chip
    label={label}
    size='small'
    variant='outlined'
    sx={{ borderRadius: 1, height: 22, fontSize: '0.7rem' }}
  />
);

type SearchFieldProps = {
  icon?: React.ReactNode;
  label: string;
  format?: string;
  examples?: string | string[];
};

const SearchField = ({ icon, label, format, examples }: SearchFieldProps) => {
  const { t } = useTranslation();
  const formattedExamples = Array.isArray(examples)
    ? examples.join(', ')
    : examples;

  return (
    <Box
      sx={{
        py: 0.5,
        px: 0.75,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction='row' spacing={2} alignItems='flex-start'>
        {icon && <Box sx={{ color: 'primary.main', opacity: 0.9 }}>{icon}</Box>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant='body2'
            fontWeight={600}
            sx={{ lineHeight: 1.3, mt: 0.25 }}
          >
            {label}
          </Typography>

          {format && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{
                fontSize: '0.7rem',
                display: 'block',
                lineHeight: 1.2,
                mt: 0.5,
              }}
            >
              {t('Format')}:{' '}
              <Box component='span' sx={{ fontFamily: 'monospace' }}>
                {format}
              </Box>
            </Typography>
          )}

          {formattedExamples && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
            >
              {t('Examples')}:{' '}
              {Array.isArray(examples) ? (
                <Stack
                  direction='row'
                  spacing={0.5}
                  flexWrap='wrap'
                  sx={{ mt: 0.25 }}
                >
                  {examples.map((ex) => (
                    <Box
                      key={ex}
                      component='span'
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: 'background.default',
                        px: 0.5,
                        borderRadius: 0.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      {ex}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box
                  component='span'
                  sx={{
                    fontFamily: 'monospace',
                    ml: 0.5,
                    fontSize: '0.7rem',
                  }}
                >
                  {formattedExamples}
                </Box>
              )}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
